import { type PlainMessage, Timestamp, toPlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import { useToken } from "@pocketsign/portal";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type CancelNotificationByPersonalInformationRequest,
  type CreateDistributionCampaignRequest,
  type CreateNotificationByPersonalInformationRequest,
  type CreateNotificationRequest,
  CreateOperationFundSourceRequest,
  type CreateStandaloneDistributionCodeRequest,
  type CreateStoreRequest,
  type DeleteStoreStaffRequest,
  type GetDistributionCampaignsRequest_Query,
  type GetUsersRequest,
  type IssueBalanceRequest,
  type SearchHokkaidoR8HouseholderCodeStatusesRequest,
  type SetFundSourceFiltersRequest,
  type UpdateStoreStaffRequest,
  type UpdateStoreRequest,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import type {
  DeleteStaffRequest,
  UpdateStaffRequest,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import type {
  HokkaidoR8HouseholderMynaApplicationResult,
  Image,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import { debugLog } from "../debug-log";
import { getTenantSlug } from "../utils";
import { AdminAccessError, PortalLoginRedirectError } from "./access-errors";
import { createAdminClient } from "./client";
import {
  type DownloadZenginParams,
  ZenginDownloadError,
  buildZenginDownloadPath,
} from "./zengin";

const useOrganizationData = () => {
  const { getToken: portalGetToken } = useToken();
  const tenantSlug = getTenantSlug();
  const getToken = async (...args: Parameters<typeof portalGetToken>) => {
    debugLog("[admin-hooks] getToken requested for admin API", {
      href: window.location.href,
      pathname: window.location.pathname,
      tenantSlug,
    });
    let token: string;
    try {
      token = await portalGetToken(...args);
    } catch (error) {
      debugLog("[admin-hooks] getToken failed for admin API", {
        href: window.location.href,
        pathname: window.location.pathname,
        tenantSlug,
        error,
      });
      if (error instanceof AdminAccessError) {
        throw error;
      }
      throw new AdminAccessError(
        {
          kind: "relogin",
          reason: "portal_token_error",
        },
        error instanceof Error ? error.message : undefined,
      );
    }

    // 空トークンは SDK がログインフローへ遷移中の合図。
    if (!token) {
      debugLog("[admin-hooks] getToken returned an empty token", {
        href: window.location.href,
        pathname: window.location.pathname,
        tenantSlug,
      });
      throw new PortalLoginRedirectError();
    }

    debugLog("[admin-hooks] getToken resolved for admin API", {
      href: window.location.href,
      pathname: window.location.pathname,
      tenantSlug,
      hasToken: true,
      tokenLength: token.length,
    });
    return token;
  };
  return { getToken, tenantSlug };
};

export const useGetMyCompanies = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: ["AdminGetMyCompanies", tenantSlug ?? ""],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getMyCompanies({});
      return res.companyWithStores.sort((a, b) => {
        if (a.company?.name && b.company?.name) {
          return a.company.name.localeCompare(b.company.name);
        }
        return 0;
      });
    },
    staleTime: 1000 * 60 * 1, // 1 minute
  });
};

export const useGetAllCities = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: ["AdminGetAllCities", tenantSlug ?? ""],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getAllCities({});
      return res.cities;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useGetCategories = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: ["AdminGetCategories", tenantSlug ?? ""],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getCategories({});
      return res.categories;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      name: string;
      ownerName: string;
      address: string;
      contact: string;
      iconUrl?: string;
      bankName: string;
      bankCode: string;
      bankBranchName: string;
      bankBranchCode: string;
      bankAccountType: string;
      bankAccountNumber: string;
      bankAccountHolderName: string;
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.createCompany(params);
      return res.companyId;
    },
    onSuccess: async (companyId) => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetMyCompanies"],
      });
      return companyId;
    },
    onError: (error) => {
      console.error("Error creating company:", error);
      throw error;
    },
  });
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      companyId: string;
      name?: string;
      ownerName?: string;
      address?: string;
      contact?: string;
      iconUrl?: string;
      bankName?: string;
      bankBranchName?: string;
      bankAccountType?: string;
      bankAccountNumber?: string;
      bankAccountHolderName?: string;
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.updateCompany(params);
      return res.companyId;
    },
    onSuccess: async (companyId) => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetMyCompanies"],
      });
      return companyId;
    },
  });
};

export const useCreateStore = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: PlainMessage<CreateStoreRequest>) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.createStore(params);
      return { companyId: res.companyId, storeId: res.storeId };
    },
    onSuccess: async (storeId) => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetMyCompanies"],
      });
      return storeId;
    },
  });
};

export const useUpdateStore = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: PlainMessage<UpdateStoreRequest>) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.updateStore(params);
      return res.storeId;
    },
    onSuccess: async (storeId) => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetMyCompanies"],
      });
      return storeId;
    },
  });
};

export const useUpdateStoreImage = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: { storeId: string; image: Image }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.updateStoreImage(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetMyCompanies"],
      });
    },
  });
};

export const useDeleteStoreParameter = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      storeId: string;
      location: boolean; // true: delete location
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.deleteStoreParameter(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetMyCompanies"],
      });
    },
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      companyId: string;
      slug?: string;
      password?: string;
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.createStaff(params);
      return {
        companyId: res.companyId,
        password: res.password,
        slug: res.slug,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["AdminGetStaffs"] });
    },
  });
};

export const useGetStaffs = (params: { companyId?: string }) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetStaffs",
      tenantSlug ?? "",
      params.companyId,
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getStaffs({
        query: { companyId: params.companyId },
      });
      return res.staffs;
    },
    enabled: !!params.companyId,
    staleTime: 1000 * 60, // 1 minute
  });
};

export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: UpdateStaffRequest) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.updateStaff(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["AdminGetStaffs"] });
    },
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: DeleteStaffRequest) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.deleteStaff(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["AdminGetStaffs"] });
    },
  });
};

export const useCreateStoreStaff = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      storeId: string;
      slug?: string;
      password?: string;
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.createStoreStaff(params);
      return {
        storeId: res.storeId,
        password: res.password,
        slug: res.slug,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["AdminGetStoreStaffs"] });
    },
  });
};

export const useGetStoreStaffs = (params: { storeId?: string }) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetStoreStaffs",
      tenantSlug ?? "",
      params.storeId,
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getStoreStaffs({
        query: { storeId: params.storeId },
      });
      return res.staffs;
    },
    enabled: !!params.storeId,
    staleTime: 1000 * 60,
  });
};

export const useUpdateStoreStaff = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: UpdateStoreStaffRequest) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.updateStoreStaff(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["AdminGetStoreStaffs"] });
    },
  });
};

export const useDeleteStoreStaff = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: DeleteStoreStaffRequest) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.deleteStoreStaff(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["AdminGetStoreStaffs"] });
    },
  });
};

export const useIssueBalance = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: PlainMessage<IssueBalanceRequest>) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.issueBalance(params);
      return {
        ...toPlainMessage(res),
      };
    },
    onSuccess: async () => {
      // invalidate search user query
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetUsers", "AdminGetUser"],
      });
    },
  });
};

export const useCreateStandaloneDistributionCode = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (
      params: PlainMessage<CreateStandaloneDistributionCodeRequest>,
    ) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const req: PlainMessage<CreateStandaloneDistributionCodeRequest> = {
        fundSourceId: params.fundSourceId,
        distributionBalanceAmount: params.distributionBalanceAmount,
        balanceExpiresAt: params.balanceExpiresAt,
        codeExpiresAt: params.codeExpiresAt,
      };
      return adminClient.createStandaloneDistributionCode(req);
    },
  });
};

export const useDownloadZengin = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation<
    { blob: Blob; fileName: string },
    Error,
    DownloadZenginParams
  >({
    mutationFn: async (params) => {
      const token = await getToken();
      const response = await fetch(buildZenginDownloadPath(params), {
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          "X-Tenant-Slug": tenantSlug ?? "",
          Accept: params.accept,
        },
      });

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        if (response.status === 401) {
          throw new AdminAccessError(
            {
              kind: "relogin",
              reason: "backend_unauthenticated",
            },
            message || "Download failed with status 401",
          );
        }
        throw new ZenginDownloadError(
          response.status,
          message || `Download failed with status ${response.status}`,
        );
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition");
      const fileName =
        contentDisposition?.match(/filename=\"?([^\";]+)\"?/)?.[1] ??
        `zengin_${params.yearMonth}.${params.accept === "text/zengin" ? "txt" : "csv"}`;

      return { blob, fileName };
    },
  });
};

// Get users

export const useGetUsers = (params: GetUsersRequest) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetUsers",
      tenantSlug ?? "",
      params.toJsonString(),
    ], // maybe FIXME. sloppy deep comparison
    queryFn: async () => {
      if (params.query === undefined) {
        return null;
      }
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getUsers(params);
      return res;
    },
    staleTime: 1000 * 60, // 1 minute
  });
};

export const useGetUser = (params: { subscriptionOrUserId?: string }) => {
  const { getToken, tenantSlug } = useOrganizationData();

  const subscriptionOrUserId = params.subscriptionOrUserId?.trim();
  return useQuery({
    queryKey: ["AdminGetUser", tenantSlug ?? "", subscriptionOrUserId],
    queryFn: async () => {
      if (!subscriptionOrUserId) {
        return null;
      }
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getUser({
        subscriptionOrUserId,
      });
      return res;
    },
    retry: (failureCount, error) => {
      // won't retry on 400 or 404
      if (error instanceof ConnectError) {
        if (
          error.code === Code.InvalidArgument ||
          error.code === Code.NotFound
        ) {
          return false;
        }
      }
      return failureCount < 3; // retry up to 3 times for other errors
    },
    staleTime: 1000 * 60, // 1 minute
  });
};

export const useGetUsersBySubscriptionIds = (params: {
  subscriptionIds: string[];
  enabled?: boolean;
}) => {
  const { getToken, tenantSlug } = useOrganizationData();

  const subscriptionIds = Array.from(
    new Set(
      params.subscriptionIds
        .map((subscriptionId) => subscriptionId.trim())
        .filter((subscriptionId) => subscriptionId.length > 0),
    ),
  );

  const queries = useQueries({
    queries: subscriptionIds.map((subscriptionId) => ({
      queryKey: ["AdminGetUser", tenantSlug ?? "", subscriptionId],
      queryFn: async () => {
        const token = await getToken();
        const adminClient = createAdminClient({
          token: token ?? "",
          tenantSlug: tenantSlug ?? "",
        });
        return adminClient.getUser({ subscriptionId });
      },
      retry: (failureCount: number, error: unknown) => {
        if (error instanceof ConnectError) {
          if (
            error.code === Code.InvalidArgument ||
            error.code === Code.NotFound
          ) {
            return false;
          }
        }
        return failureCount < 3;
      },
      staleTime: 1000 * 60, // 1 minute
      enabled: params.enabled ?? true,
    })),
  });

  return {
    subscriptionIds,
    queries,
  };
};

export const useGetDistributionCampaigns = (props: {
  query?: PlainMessage<GetDistributionCampaignsRequest_Query>;
}) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetDistributionCampaigns",
      tenantSlug ?? "",
      props.query?.fundSourceId,
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getDistributionCampaigns({
        query: props.query,
      });
      return res.distributionCampaigns.sort((a, b) => {
        if (a.id && b.id) {
          return a.id.localeCompare(b.id);
        }
        return 0;
      });
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useCreateDistributionCampaign = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (
      params: PlainMessage<CreateDistributionCampaignRequest>,
    ) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.createDistributionCampaign(params);
      return res.distributionCampaignId;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetDistributionCampaigns"],
      });
    },
  });
};

export const useGetDistributionCodes = (props: {
  distributionCampaignId: string;
  shouldIncludeConsumed?: boolean; // default to false
}) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetDistributionCodes",
      props.distributionCampaignId,
      props.shouldIncludeConsumed,
      tenantSlug ?? "",
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getDistributionCodes({
        query: {
          search: {
            case: "campaignSearch",
            value: {
              distributionCampaignId: props.distributionCampaignId,
              shouldIncludeConsumed: props.shouldIncludeConsumed ?? false,
            },
          },
        },
      });
      return res.codeUsages
        .flatMap(({ distributionCode }) =>
          distributionCode ? [distributionCode.code] : [],
        )
        .sort((a, b) => a.localeCompare(b));
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: false,
  });
};

export type DistributionCodeSearchTarget =
  | "distribution_codes"
  | "standalone_distribution_codes";

export type DistributionCodeSearchResult = {
  code: string;
  subscriptionId?: string;
  usedAt?: Timestamp;
};

export const useSearchDistributionCodes = (props: {
  target: DistributionCodeSearchTarget;
  fundSourceId?: string;
  code?: string;
}) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminSearchDistributionCodes",
      props.target,
      props.fundSourceId,
      props.code,
      tenantSlug ?? "",
    ],
    queryFn: async (): Promise<DistributionCodeSearchResult[]> => {
      if (!props.code || !props.fundSourceId) {
        return [];
      }
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });

      if (props.target === "distribution_codes") {
        const [campaignsResponse, codesResponse] = await Promise.all([
          adminClient.getDistributionCampaigns({
            query: { fundSourceId: props.fundSourceId },
          }),
          adminClient.getDistributionCodes({
            query: {
              search: {
                case: "codeSearch",
                value: { codePrefix: props.code },
              },
            },
          }),
        ]);
        const campaignIds = new Set(
          campaignsResponse.distributionCampaigns.map(
            (campaign) => campaign.id,
          ),
        );
        return codesResponse.codeUsages.flatMap(
          ({ distributionCode, consumer }) =>
            distributionCode &&
            campaignIds.has(distributionCode.distributionCampaignId)
              ? [
                  {
                    code: distributionCode.code,
                    subscriptionId: consumer?.subscriptionId,
                    usedAt: distributionCode.userId
                      ? distributionCode.updatedAt
                      : undefined,
                  },
                ]
              : [],
        );
      }

      const res = await adminClient.getStandaloneDistributionCode({
        code: props.code,
      });
      const distributionCode = res.standaloneDistributionCode;
      return distributionCode?.fundSourceId === props.fundSourceId
        ? [
            {
              code: distributionCode.code,
              subscriptionId: res.consumer?.subscriptionId,
              usedAt: distributionCode.userId
                ? distributionCode.updatedAt
                : undefined,
            },
          ]
        : [];
    },
    enabled: props.code !== undefined && props.fundSourceId !== undefined,
    staleTime: 1000 * 60,
  });
};

// Operation FundSourceのtype値（上に表示）
const operationTypes = [
  "AdminGrant",
  "PSInternal",
  "Distributable",
  "StandaloneDistributable",
];

export const useGetAllFundSources = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetAllFundSources",
      tenantSlug ?? "",
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getAllFundSources({});
      // Operation FSを先に（created_at DESC順維持）、Campaign FSを後に
      const operationFS = res.fundSources.filter((fs) =>
        operationTypes.includes(fs.type),
      );
      const campaignFS = res.fundSources.filter(
        (fs) => !operationTypes.includes(fs.type),
      );
      return [...operationFS, ...campaignFS];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useGetFundSourceFilters = (props: { fundSourceId?: string }) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetFundSourceFilters",
      tenantSlug ?? "",
      props.fundSourceId ?? "",
    ],
    queryFn: async () => {
      if (!props.fundSourceId) {
        return [];
      }
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getFundSourceFilters({
        query: {
          fundSourceId: props.fundSourceId,
        },
      });
      return res.filters;
    },
    enabled: Boolean(props.fundSourceId),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useSetFundSourceFilters = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: PlainMessage<SetFundSourceFiltersRequest>) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.setFundSourceFilters(params);
    },
    onSuccess: async (_data, params) => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetFundSourceFilters"],
      });
    },
  });
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: PlainMessage<CreateNotificationRequest>) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.createNotification(params);
      return res.notificationHistoryId;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetNotificationHistories"],
      });
    },
  });
};

export const useCreateOperationFundSource = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (
      params: PlainMessage<CreateOperationFundSourceRequest>,
    ) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const req = new CreateOperationFundSourceRequest(params);
      const res = await adminClient.createOperationFundSource(req);
      return res.operationFundSource?.id ?? "";
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetAllFundSources"],
      });
    },
  });
};

export const useCreateNotificationByPersonalInformation = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (
      params: PlainMessage<CreateNotificationByPersonalInformationRequest>,
    ) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res =
        await adminClient.createNotificationByPersonalInformation(params);
      return res.notificationByPersonalInformationHistoryId;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetNotificationByPersonalInformationHistories"],
      });
    },
  });
};

export const useCancelNotificationByPersonalInformation = () => {
  const queryClient = useQueryClient();
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (
      params: PlainMessage<CancelNotificationByPersonalInformationRequest>,
    ) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.cancelNotificationByPersonalInformation(params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["AdminGetNotificationByPersonalInformationHistories"],
      });
    },
  });
};

export const useGetNotificationHistories = (params: { userId?: string }) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetNotificationHistories",
      tenantSlug ?? "",
      params.userId,
    ],
    queryFn: async () => {
      if (!params.userId) {
        return [];
      }
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getNotificationHistories({
        userId: params.userId,
      });
      return res.notificationHistories;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGetNotificationByPersonalInformationHistories = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminGetNotificationByPersonalInformationHistories",
      tenantSlug ?? "",
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res =
        await adminClient.getNotificationByPersonalInformationHistories({});
      return res.notificationByPersonalInformationHistories;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUploadHokkaidoHouseholderCSV = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: { filename: string; content: Uint8Array }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.uploadHokkaidoHouseholderCSV(params);
      return res.objectKey;
    },
  });
};

export const useCreateHokkaidoR8HouseholderCodeUsesAsPaperVoucher = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: { codes: string[] }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.createHokkaidoR8HouseholderCodeUsesAsPaperVoucher(params);
    },
  });
};

export const useSearchHokkaidoR8HouseholderCodeStatuses = (
  params: SearchHokkaidoR8HouseholderCodeStatusesRequest,
  enabled: boolean,
) => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useQuery({
    queryKey: [
      "AdminSearchHokkaidoR8HouseholderCodeStatuses",
      tenantSlug ?? "",
      params.toJsonString(),
    ],
    queryFn: async () => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      return adminClient.searchHokkaidoR8HouseholderCodeStatuses(params);
    },
    enabled,
    retry: (failureCount, error) => {
      // won't retry on 400
      if (error instanceof ConnectError && error.code === Code.InvalidArgument) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 1000 * 30, // 30 seconds
  });
};

export const useCreateHokkaidoR8HouseholderPostalReturns = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: { codes: string[]; reason: string }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.createHokkaidoR8HouseholderPostalReturns(params);
    },
  });
};

export const useGetHokkaidoR8HouseholderPaperVouchersByPeriod = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: { start: Date; end: Date }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getHokkaidoR8HouseholderPaperVouchersByPeriod({
        issuedAtStart: Timestamp.fromDate(params.start),
        issuedAtEnd: Timestamp.fromDate(params.end),
      });
      return res.rows;
    },
  });
};

export const useGetHokkaidoR8HouseholderPostalReturnsByPeriod = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: { start: Date; end: Date }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.getHokkaidoR8HouseholderPostalReturnsByPeriod({
        returnedAtStart: Timestamp.fromDate(params.start),
        returnedAtEnd: Timestamp.fromDate(params.end),
      });
      return res.rows;
    },
  });
};

export const useExportHokkaidoR8HouseholderMynaApplications = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      start: Date;
      end: Date;
      results: HokkaidoR8HouseholderMynaApplicationResult[];
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      const res = await adminClient.exportHokkaidoR8HouseholderMynaApplications({
        appliedAtStart: Timestamp.fromDate(params.start),
        appliedAtEnd: Timestamp.fromDate(params.end),
        results: params.results,
      });
      return res.rows;
    },
  });
};

export const useCreateHokkaidoR8HouseholderCodeUsesAsStaffMatch = () => {
  const { getToken, tenantSlug } = useOrganizationData();
  return useMutation({
    mutationFn: async (params: {
      items: { applicationId: string; code: string }[];
    }) => {
      const token = await getToken();
      const adminClient = createAdminClient({
        token: token ?? "",
        tenantSlug: tenantSlug ?? "",
      });
      await adminClient.createHokkaidoR8HouseholderCodeUsesAsStaffMatch(params);
    },
  });
};
